import React, { memo, useState } from "react"

import useTranslation from "Pods/ThreeDStudio/Hooks/useTranslation"
import { useViewerStore } from "Pods/ThreeDStudio/Hooks/useViewerStore"
import { PROVIDER_LABELS } from "Pods/ThreeDStudio/Shared/AIEngineSelector/providerLabels"
import { useViewerContext } from "Pods/ThreeDStudio/Shared/ViewerContext/ViewerContext"
import { useThreeDStudioStore } from "Pods/ThreeDStudio/Store"
import { MATERIAL, POLYGON_TYPE } from "Pods/ThreeDStudio/Store/constants"
import {
  selectOpenedModelGenerationSettings,
} from "Pods/ThreeDStudio/Store/selectors/selectOpenedModelGenerationSettings"

import CardBody from "./Components/CardBody/CardBody"
import CompactCard from "./Components/CompactCard/CompactCard"
import { EMPTY_VALUE, formatInteger } from "./Utils/formatInteger"

const TOPOLOGY_LABELS = {
  quad: 'Quad',
  triangle: 'Triangle',
}

const POLYGON_TYPE_LABELS = {
  [POLYGON_TYPE.TRIANGLE]: 'Triangle',
  [POLYGON_TYPE.QUADRILATERAL]: 'Quad',
}

function ModelDetailsCard() {
  const t = useTranslation()
  const viewer = useViewerContext()
  const stats = useViewerStore(viewer, "stats")
  const [isExpanded, setIsExpanded] = useState(false)

  const providerModelId = useThreeDStudioStore(state => state.ui?.openedModel?.providerModelId)
  // Derived from the immutable openedModel, not the live modelSettings / schema
  // slices the generation panel mutates. This keeps the panel fixed on the
  // settings that generated the displayed model.
  const {
    generationProvider,
    material,
    targetPolycount,
    polygonType,
    isSmartLowPoly,
    extraQuality,
  } = useThreeDStudioStore(selectOpenedModelGenerationSettings)

  if (!viewer || !stats) {
    return null
  }

  const { triangleCount, vertexCount, topologyMode } = stats

  // Settings rows only exist for opened library runs; uploaded models have no
  // generation metadata, so the card stays at the base three rows.
  const hasSettings = Boolean(providerModelId)

  const materialLabels = {
    [MATERIAL.PBR]: 'PBR',
    [MATERIAL.BASIC]: t('viewer.modelDetails.materialBasic'),
    [MATERIAL.GEOMETRY_ONLY]: t('viewer.modelDetails.materialGeometryOnly'),
  }

  // An upload carries no generation settings, so its polygon type can only be
  // read back off the loaded mesh.
  const polygonTypeValue = hasSettings
    ? POLYGON_TYPE_LABELS[polygonType]
    : TOPOLOGY_LABELS[topologyMode]

  const baseRows = [
    { label: t('viewer.modelDetails.polygonType'), value: polygonTypeValue || EMPTY_VALUE },
    { label: t('viewer.modelDetails.faces'), value: formatInteger(triangleCount) },
    { label: t('viewer.modelDetails.vertices'), value: formatInteger(vertexCount) },
  ]

  // Unknown (e.g. an enhancement without a linked source run) hides the row
  // rather than asserting "false".
  let lowPolyValue = EMPTY_VALUE
  if (isSmartLowPoly != null) {
    lowPolyValue = isSmartLowPoly ? t('viewer.modelDetails.true') : t('viewer.modelDetails.false')
  }

  const settingsRows = [
    {
      label: t('viewer.modelDetails.aiEngine'),
      value: PROVIDER_LABELS[generationProvider] || EMPTY_VALUE,
      tag: extraQuality ? t('viewer.modelDetails.extraQuality') : undefined,
    },
    { label: t('viewer.modelDetails.lowPoly'), value: lowPolyValue },
    { label: t('viewer.modelDetails.polycount'), value: formatInteger(targetPolycount) },
    { label: t('viewer.modelDetails.material'), value: materialLabels[material] || EMPTY_VALUE },
  ]

  const rows = (hasSettings && isExpanded ? [...baseRows, ...settingsRows] : baseRows)
    .filter(row => row.value !== EMPTY_VALUE)

  const handleToggle = () => setIsExpanded(prev => !prev)

  const cardBody = (
    <CardBody rows={rows} hasSettings={hasSettings} isExpanded={isExpanded} onToggle={handleToggle} />
  )

  return (
    <>
      <div className="model-details-card model-details-card--inline">
        {cardBody}
      </div>
      <CompactCard>{cardBody}</CompactCard>
    </>
  )
}

export default memo(ModelDetailsCard)
